import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkGetReferralRewardsResponse,
  QueryReferralRewardsOptions,
  QueryReferralRewardsResponse,
  ReferralReward,
  ReferralRewardQuery,
  ReferralRewardsQueryBuilder,
  bulkGetReferralRewards as universalBulkGetReferralRewards,
  getReferralReward as universalGetReferralReward,
  queryReferralRewards as universalQueryReferralRewards,
  typedQueryReferralRewards as universalTypedQueryReferralRewards,
} from './loyalty-referral-v1-referral-reward-rewards.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/referral' };

export function getReferralReward(
  httpClient: HttpClient
): GetReferralRewardSignature {
  return (_id: string) =>
    universalGetReferralReward(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface GetReferralRewardSignature {
  /**
   * Retrieves a referral reward.
   * @param - Referral reward ID.
   * @returns Retrieved referral reward.
   */
  (_id: string): Promise<
    NonNullablePaths<
      ReferralReward,
      | `rewardedReferringCustomerId`
      | `rewardedReferredFriendId`
      | `coupon._id`
      | `coupon.code`
      | `coupon.status`
      | `coupon.couponSpecification.fixedAmountOptions.amount`
      | `coupon.couponSpecification.percentageOptions.percentage`
      | `coupon.couponSpecification.minimumSubtotal`
      | `coupon.couponSpecification.scope.namespace`
      | `coupon.couponSpecification.scope.group.name`
      | `coupon.couponSpecification.name`
      | `coupon.couponSpecification.discountType`
      | `loyaltyPoints.transactionId`
      | `loyaltyPoints.amount`
      | `rewardType`,
      6
    >
  >;
}

export function queryReferralRewards(
  httpClient: HttpClient
): QueryReferralRewardsSignature {
  return (options?: QueryReferralRewardsOptions) =>
    universalQueryReferralRewards(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryReferralRewardsSignature {
  /**
   * Creates a query to retrieve a list of referral rewards.
   *
   * The `queryReferralRewards()` function builds a query to retrieve a list of events and returns a `ReferralRewardsQueryBuilder` object.
   *
   * The returned object contains the query definition, which is typically used to run the query using the `find()` function.
   *
   * You can refine the query by chaining `ReferralRewardsQueryBuilder` functions onto the query. `ReferralRewardsQueryBuilder` functions enable you to sort, filter, and control the results `queryReferralRewards()` returns.
   *
   * `queryReferralRewards()` runs with these `ReferralRewardsQueryBuilder` defaults, which you can override:
   *
   * - `limit(50)`
   * - `descending("_createdDate")`
   *
   * The functions that are chained to `queryReferralRewards()` are applied in the order they're called. For example, if you apply ascending('rewardedReferredFriendId') and then descending('rewardType'), the results are sorted first by the referred friend ID, and then, if there are multiple results with the same ID, the items are sorted by reward type.
   */
  (options?: QueryReferralRewardsOptions): ReferralRewardsQueryBuilder;
}

export function typedQueryReferralRewards(
  httpClient: HttpClient
): TypedQueryReferralRewardsSignature {
  return (query: ReferralRewardQuery, options?: QueryReferralRewardsOptions) =>
    universalTypedQueryReferralRewards(
      query,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryReferralRewardsSignature {
  /** */
  (query: ReferralRewardQuery, options?: QueryReferralRewardsOptions): Promise<
    NonNullablePaths<
      QueryReferralRewardsResponse,
      | `referralRewards`
      | `referralRewards.${number}.rewardedReferringCustomerId`
      | `referralRewards.${number}.rewardedReferredFriendId`
      | `referralRewards.${number}.coupon._id`
      | `referralRewards.${number}.coupon.code`
      | `referralRewards.${number}.coupon.status`
      | `referralRewards.${number}.coupon.couponSpecification.minimumSubtotal`
      | `referralRewards.${number}.coupon.couponSpecification.name`
      | `referralRewards.${number}.coupon.couponSpecification.discountType`
      | `referralRewards.${number}.loyaltyPoints.transactionId`
      | `referralRewards.${number}.loyaltyPoints.amount`
      | `referralRewards.${number}.rewardType`,
      6
    >
  >;
}

/** @internal */
export function bulkGetReferralRewards(
  httpClient: HttpClient
): BulkGetReferralRewardsSignature {
  return () =>
    universalBulkGetReferralRewards(
      // @ts-ignore
      { httpClient }
    );
}

interface BulkGetReferralRewardsSignature {
  /**
   * Retrieves rewards from all metasites that the caller is the member of.
   *
   * This method must be called with a [user identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities#wix-users).
   */
  (): Promise<
    NonNullablePaths<
      BulkGetReferralRewardsResponse,
      `rewardsInSite` | `rewardsInSite.${number}.metaSiteId`,
      4
    >
  >;
}

export {
  AccountInfo,
  ActionEvent,
  BulkGetReferralRewardsRequest,
  BulkGetReferralRewardsResponse,
  Coupon,
  CouponDiscountTypeOptionsOneOf,
  CouponScope,
  CouponScopeOrMinSubtotalOneOf,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  DiscountType,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  FixedAmountDiscount,
  GetReferralRewardRequest,
  GetReferralRewardResponse,
  Group,
  IdentificationData,
  IdentificationDataIdOneOf,
  LoyaltyPoints,
  MessageEnvelope,
  PercentageDiscount,
  QueryReferralRewardsOptions,
  QueryReferralRewardsRequest,
  QueryReferralRewardsResponse,
  ReferralReward,
  ReferralRewardQuerySpec,
  ReferralRewardReceiverOneOf,
  ReferralRewardRewardTypeOptionsOneOf,
  ReferralRewardsQueryBuilder,
  ReferralRewardsQueryResult,
  ReferredFriendDetails,
  RestoreInfo,
  Reward,
  RewardOptionsOneOf,
  RewardTypeType,
  RewardsInSite,
  SortOrder,
  Sorting,
  Status,
  SuccessfulReferralEvent,
  Type,
  V1Coupon,
  V1LoyaltyPoints,
  ValidateReferralRewardRequest,
  ValidateReferralRewardResponse,
  WebhookIdentityType,
  utils,
} from './loyalty-referral-v1-referral-reward-rewards.universal.js';
